import React from 'react';
import {
  ApAuthenticationContextProvider,
  Telemetry,
} from '@alixpartners/ui-utils';
import { ScreenClassRender, setConfiguration } from 'react-grid-system';
import { createBrowserHistory } from 'history';
import {
  Router,
  Route,
  Redirect,
  Switch,
  RouteComponentProps,
} from 'react-router-dom';

import { gutters, getGridConfig } from './styles/gridConfig';
import { Analysis } from './views/Analysis';
import { Applications } from './views/Applications';
import { BenchmarksContainer } from './views/Benchmarks';
import { Products } from './views/Products';
import { Settings } from './views/Settings';
import { Classification } from 'views/Classification';
import {
  ApplicationAllocation,
  ROOT_ROUTE as APP_ALLOCATION_ROUTE,
} from 'views/SpendAllocation/ApplicationAllocation';
import {
  ProductAllocation,
  ROOT_ROUTE as PRODUCT_ALLOCATION_ROUTE,
} from 'views/SpendAllocation/ProductAllocation';
import { ErrorPage } from './views/Error';
import { DownloadFile } from './views/DownloadFile/DownloadFile';
import { AppRoot, MainContainer, AccessDeniedContainer } from './App.styles';
import ApiProvider from './context/ApiContext';
import { Header } from './components/Header';
import { ApAccessDenied } from '@alixpartners/ui-components';
import { ApiContext } from 'context/ApiContext';
import { PERMISSIONS } from 'utils/constants';

const history = createBrowserHistory();
Telemetry.setup(process.env.REACT_APP_AI_KEY, history);

function App() {
  return (
    <ApAuthenticationContextProvider
      errorComponent={({ errorType, errorDescription, onClick }) => (
        <ErrorPage
          title={errorType}
          description={errorDescription}
          onClick={onClick}
        />
      )}
    >
      <AppRoot>
        <Router history={history}>
          <Switch>
            <Route
              exact
              path="/accessdenied"
              render={({ location }: RouteComponentProps<any, any, any>) => (
                <>
                  <Header />
                  <MainContainer>
                    <AccessDeniedContainer>
                      <ApAccessDenied
                        name={location.state && location.state.aadName}
                        url={location.state && location.state.aadUrl}
                      />
                    </AccessDeniedContainer>
                  </MainContainer>
                </>
              )}
            />
            <ApiProvider>
              <ApiContext.Consumer>
                {({ permissions }) => {
                  const canViewBenchmarks = permissions.includes(
                    PERMISSIONS.BENCHMARKS_ANALYSIS_READ
                  );
                  const canClassifyData = permissions.includes(
                    PERMISSIONS.UNPUBLISHED_DATA_READ
                  );

                  return (
                    <React.Fragment>
                      <Header permissions={permissions} />
                      <MainContainer>
                        <ScreenClassRender
                          render={(screenClass: keyof typeof gutters) => {
                            setConfiguration({
                              ...(getGridConfig(screenClass) as any),
                            });
                            // This is where we will return the routes
                            return (
                              <Switch>
                                <Route path="/signin" exact>
                                  <Redirect to="/spend" />
                                </Route>
                                <Route path="/spend" component={Analysis} />
                                {canViewBenchmarks && (
                                  <Route
                                    path="/benchmarks"
                                    component={BenchmarksContainer}
                                  />
                                )}
                                <Route
                                  path="/applications"
                                  component={Applications}
                                />
                                <Route path="/settings" component={Settings} />
                                {canClassifyData && (
                                  <Route
                                    path="/classification"
                                    component={Classification}
                                  />
                                )}
                                <Route
                                  path={APP_ALLOCATION_ROUTE}
                                  component={ApplicationAllocation}
                                />
                                <Route
                                  path={PRODUCT_ALLOCATION_ROUTE}
                                  component={ProductAllocation}
                                />
                                <Route path="/products" component={Products} />
                                <Route
                                  path="/files/:fileName"
                                  component={DownloadFile}
                                />
                                <Redirect to="/spend" />
                              </Switch>
                            );
                          }}
                        />
                      </MainContainer>
                    </React.Fragment>
                  );
                }}
              </ApiContext.Consumer>
            </ApiProvider>
          </Switch>
        </Router>
      </AppRoot>
    </ApAuthenticationContextProvider>
  );
}

export default App;
